import React, { useEffect, useState } from 'react';
import { api } from '../../../services/apiServices';
import { Badge } from '../../../components/ui/badge';
import { Check, Plus, X, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Input } from '../../../components/ui/input';

const LocationTagSelector = ({ selectedIds = [], onChange }) => {
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Load tags
  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = () => {
    api.get('/admin/seo/location-tags').then(res => setTags(res.data)).catch(console.error);
  };

  const toggleTag = (id) => {
    const newIds = selectedIds.includes(id) 
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id];
    onChange(newIds);
  };

  // --- Inline Creation Logic ---
  const handleCreateTag = async () => {
    if (!search.trim()) return;
    setIsCreating(true);
    try {
        const res = await api.post('/admin/seo/location-tags', { name: search });
        const newTag = res.data;
        setTags([...tags, newTag]); // Add to local list
        onChange([...selectedIds, newTag.id]); // Auto-select it
        setSearch(''); // Clear search
    } catch (error) {
        alert("Failed to create tag. It might already exist.");
    } finally {
        setIsCreating(false);
    }
  };

  const selectedTags = tags.filter(t => selectedIds.includes(t.id));
  const filteredTags = tags.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-2">
        {/* Selected Tags Display */}
        <div className="flex flex-wrap gap-2 mb-2">
            {selectedTags.map(tag => (
                <Badge key={tag.id} variant="secondary" className="pl-2 pr-1 py-1 gap-1 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100">
                    {tag.name}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => toggleTag(tag.id)}/>
                </Badge>
            ))}
            {selectedTags.length === 0 && <span className="text-xs text-gray-400 italic">No tags selected</span>}
        </div>
        
        {/* Dropdown */}
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 border-dashed gap-1 text-xs">
                    <Plus className="w-3 h-3" /> Add Tags
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-2" align="start">
                
                {/* Search / Create Input */}
                <div className="mb-2">
                    <Input 
                        placeholder="Search or Create..." 
                        className="h-8 text-xs" 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="max-h-[200px] overflow-auto space-y-1">
                    {/* List Existing Matches */}
                    {filteredTags.map(tag => (
                        <div 
                            key={tag.id} 
                            onClick={() => toggleTag(tag.id)}
                            className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer text-sm"
                        >
                            <span>{tag.name}</span>
                            {selectedIds.includes(tag.id) && <Check className="w-3 h-3 text-blue-600"/>}
                        </div>
                    ))}

                    {/* Create New Option (if no exact match) */}
                    {search && !filteredTags.find(t => t.name.toLowerCase() === search.toLowerCase()) && (
                        <div 
                            onClick={handleCreateTag}
                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-blue-50 text-blue-600 rounded cursor-pointer text-xs font-medium border-t mt-1"
                        >
                            {isCreating ? <Loader2 className="w-3 h-3 animate-spin"/> : <Plus className="w-3 h-3" />}
                            Create "{search}"
                        </div>
                    )}
                    
                    {filteredTags.length === 0 && !search && (
                        <p className="text-xs text-gray-400 text-center py-2">No tags found.</p>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    </div>
  );
};

export default LocationTagSelector;